import type { Json } from "@redotech/json/json";
import { isPlainObject } from "@redotech/util/object";
import * as z from "zod";
import type { Condition } from "../return-flow/condition";
import { conditionJsonFormat } from "../return-flow/condition";

/** The one bridge from a stored leaf to a flow condition; throws on an unknown or malformed one. */
export function readFlowConditionLeaf(leaf: FlowConditionLeaf): Condition {
  const json: Json = JSON.parse(JSON.stringify(leaf));
  return conditionJsonFormat.read(json);
}

const FlowConditionLeafSchema = z
  .object({ type: z.string() })
  .passthrough()
  .superRefine((leaf, ctx) => {
    try {
      readFlowConditionLeaf(leaf);
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unknown condition ${leaf.type}: ${String(error)}`,
      });
    }
  });

export const QuestionAnswerLeafSchema = z.strictObject({
  type: z.literal("question_answer"),
  questionId: z.string(),
  optionIds: z.array(z.string()),
});

export const AiGuidanceLeafSchema = z.strictObject({
  type: z.literal("ai_guidance_result"),
  guidanceId: z.string(),
});

const ConditionLeafSchema = z.union([
  QuestionAnswerLeafSchema,
  AiGuidanceLeafSchema,
  FlowConditionLeafSchema,
]);

export interface QuestionAnswerLeaf {
  type: "question_answer";
  questionId: string;
  optionIds: string[];
}
export interface AiGuidanceLeaf {
  type: "ai_guidance_result";
  guidanceId: string;
}
/** A flow condition by its stored name; `conditionJsonFormat` owns the rest of its fields. */
export interface FlowConditionLeaf {
  type: string;
}

/**
 * A flow-condition leaf for tests and builders. Fields besides `type` are
 * whatever `conditionJsonFormat` stores; they are not part of `FlowConditionLeaf`.
 */
export function flowCondition(
  leaf: { type: string } & Record<string, unknown>,
): ConditionExpr {
  return leaf;
}
export type ConditionLeaf =
  | QuestionAnswerLeaf
  | AiGuidanceLeaf
  | FlowConditionLeaf;

export const itemQuantifiers = ["all", "any", "none"] as const;
export type ItemQuantifier = (typeof itemQuantifiers)[number];

/** `items` quantifies `where` over the items being returned, as the finalize flow does per condition. */
export type ConditionExpr =
  | ConditionLeaf
  | { all: ConditionExpr[] }
  | { any: ConditionExpr[] }
  | { not: ConditionExpr }
  | { items: ItemQuantifier; where: ConditionExpr };

export const ConditionExprSchema: z.ZodType<ConditionExpr> = z.lazy(() =>
  z.union([
    z.strictObject({ all: z.array(ConditionExprSchema) }),
    z.strictObject({ any: z.array(ConditionExprSchema) }),
    z.strictObject({ not: ConditionExprSchema }),
    z.strictObject({
      items: z.enum(itemQuantifiers),
      where: ConditionExprSchema,
    }),
    ConditionLeafSchema,
  ]),
);

function ruleSchema<V extends z.ZodTypeAny, W extends z.ZodTypeAny>(
  value: V,
  when: W,
) {
  return z.strictObject({
    id: z.string(),
    setId: z.string().optional(),
    when,
    value,
  });
}

/** First matching rule wins, so every rule needs a condition; the default covers the rest. */
export function firstMatchSchema<V extends z.ZodTypeAny>(value: V) {
  return z.strictObject({
    default: value,
    rules: z.array(ruleSchema(value, ConditionExprSchema)),
  });
}

/** Every matching rule applies; a `null` condition always applies. */
export function everyMatchSchema<V extends z.ZodTypeAny>(value: V) {
  return z.array(ruleSchema(value, ConditionExprSchema.nullable()));
}

export const compensationMethods = [
  "exchange",
  "storeCredit",
  "refund",
] as const;
export type CompensationMethod = (typeof compensationMethods)[number];

/** One first-match setting per compensation method, decided before the method is picked. */
export function perMethodFirstMatchSchema<V extends z.ZodTypeAny>(value: V) {
  return z.strictObject({
    exchange: firstMatchSchema(value),
    storeCredit: firstMatchSchema(value),
    refund: firstMatchSchema(value),
  });
}

export interface Rule<V, W = ConditionExpr> {
  id: string;
  setId?: string;
  when: W;
  value: V;
}
export interface FirstMatch<V> {
  default: V;
  rules: Rule<V>[];
}
/** A rule of either shape; `when` is null only in an every-match list. */
export type PolicyRule<V> = Rule<V, ConditionExpr | null>;
export type EveryMatch<V> = PolicyRule<V>[];

export function isFirstMatch(value: unknown): value is FirstMatch<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    "default" in value &&
    "rules" in value &&
    Array.isArray(value.rules)
  );
}

export function isEveryMatch(value: unknown): value is EveryMatch<unknown> {
  return Array.isArray(value);
}

export type PerMethodFirstMatch<V> = Record<CompensationMethod, FirstMatch<V>>;

export function isPerMethodFirstMatch(
  value: unknown,
): value is PerMethodFirstMatch<unknown> {
  return (
    isPlainObject(value) &&
    compensationMethods.every((method) => isFirstMatch(value[method]))
  );
}
